import { Box, Toolbar, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { Outlet } from "react-router-dom";
import { sessionManager } from "../context/sessionManager";
import { useSessionCheck } from "../hooks/useSessionCheck";
import SessionIndicator from "../components/SessionIndicator";

const MainLayout = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useSessionCheck();

  const handleAutoLogout = useCallback(() => {
    sessionManager.clearSession();
    localStorage.removeItem("appAuth");
    navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const sessionCheckInterval = setInterval(() => {
      if (!sessionManager.isValidSession()) {
        handleAutoLogout();
      }
    }, 30000);

    const handleSessionExpired = () => {
      handleAutoLogout();
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      clearInterval(sessionCheckInterval);
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [navigate, handleAutoLogout]);

  const handleMenuClick = () => {
    console.log("Menu clicked, opening drawer");
    setMobileDrawerOpen(true);
  };

  const handleMobileDrawerClose = () => {
    console.log("Closing drawer");
    setMobileDrawerOpen(false);
  };

  console.log("MainLayout - isMobile:", isMobile, "mobileDrawerOpen:", mobileDrawerOpen);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Navbar - Fixed at top */}
      <Navbar sidebarOpen={sidebarOpen} onMenuClick={handleMenuClick}>
        <SessionIndicator />
      </Navbar>

      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        mobileOpen={mobileDrawerOpen}
        onMobileClose={handleMobileDrawerClose}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          transition: 'margin-left 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar />
        <SessionWarningBanner />
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

// SessionWarningBanner component remains exactly the same...
const SessionWarningBanner = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const checkSessionWarning = () => {
      const remainingTime = sessionManager.getRemainingTime();
      const minutes = Math.floor(remainingTime / (1000 * 60));

      if (minutes <= 5 && minutes > 0) {
        setMessage(`⚠️ Your session will expire in ${minutes} minute${minutes !== 1 ? 's' : ''}.`);
        setShowWarning(true);
      } else if (minutes <= 0) {
        setMessage("Session expired. Redirecting to login...");
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    };

    const interval = setInterval(checkSessionWarning, 30000);
    checkSessionWarning();

    return () => clearInterval(interval);
  }, []);

  const refreshSession = () => {
    if (sessionManager.refreshSession()) {
      setShowWarning(false);
    }
  };

  if (!showWarning) return null;

  return (
    <Box
      sx={{
        bgcolor: 'warning.light',
        color: 'warning.contrastText',
        py: 1,
        px: 2,
        textAlign: 'center',
        fontSize: '0.875rem',
        fontWeight: 'medium',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {message}
      <Typography
        component="span"
        sx={{
          textDecoration: 'underline',
          cursor: 'pointer',
          fontWeight: 'bold',
          ml: 1
        }}
        onClick={refreshSession}
      >
        Refresh Session
      </Typography>
    </Box>
  );
};

export default MainLayout;